/**
 * System (curator / VITALL staff) list view for EHR sync monitoring: the
 * environment line with Register connection, summary cards, the filter toolbar
 * and the sortable connections table. Rows drill into MonitoringDetail.
 */
import { Box, Button } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';

import * as ROUTES from '@constants/routes';

import {
  isServiceUnreachable,
  useEhrSyncConnections,
} from '@hooks/api/ehr-sync';
import { useNavigate } from '@hooks/useNavigate';
import { useSnackbar } from '@hooks/useSnackbar';

import RegisterConnectionDialog from '../../components/RegisterConnectionDialog';
import { commonMessages } from '../../messages';
import StatePanel from '../shared/StatePanel';
import { effectiveStatus } from '../shared/monitoring.util';
import ConnectionsTable from './ConnectionsTable';
import EnvironmentBanner from './EnvironmentBanner';
import FilterBar from './FilterBar';
import type { StatusFilter, VendorFilter } from './FilterBar';
import SummaryCards from './SummaryCards';

const messages = defineMessages({
  unreachableBody: {
    id: 'ehrSync.list.unreachableBody',
    defaultMessage:
      "EHR connection status couldn't be loaded because the integration service didn't respond. Try again in a moment.",
  },
  register: {
    id: 'ehrSync.list.register',
    defaultMessage: 'Register connection',
  },
  registered: {
    id: 'ehrSync.list.registered',
    defaultMessage:
      '{zone} registered. It shows as Provisioning until its channels run.',
  },
  emptyTitle: {
    id: 'ehrSync.list.emptyConnectionsTitle',
    defaultMessage: 'No EHR connections yet',
  },
  emptyBody: {
    id: 'ehrSync.list.emptyConnectionsBody',
    defaultMessage:
      'Connections appear here once they are registered for this Engage environment.',
  },
});

export default function MonitoringList() {
  const { formatMessage } = useIntl();
  const { data, loading, error, refetch } = useEhrSyncConnections();
  const { navigateRoute } = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [registerOpen, setRegisterOpen] = useState(false);
  // Mounted on first open and kept, so the accounts query waits for someone to
  // register and the dialog still animates out.
  const [registerMounted, setRegisterMounted] = useState(false);

  const [status, setStatus] = useState<StatusFilter>('all');
  const [vendor, setVendor] = useState<VendorFilter>('all');
  const [query, setQuery] = useState('');

  const vendors = useMemo(
    () =>
      Array.from(
        new Set(
          data
            .map((connection) => connection.ehrVendor)
            .filter((vendor): vendor is string => Boolean(vendor))
        )
      ).sort(),
    [data]
  );

  const filtered = useMemo(() => {
    const search = query.trim().toLowerCase();
    return data.filter((connection) => {
      if (status !== 'all' && effectiveStatus(connection) !== status) {
        return false;
      }
      if (vendor !== 'all' && connection.ehrVendor !== vendor) {
        return false;
      }
      return (
        !search || connection.organizationName.toLowerCase().includes(search)
      );
    });
  }, [data, status, vendor, query]);

  let content: React.ReactNode;
  if (error) {
    // Shown in place rather than as an empty table, which would read as a
    // healthy "zero connections" result and hide the outage.
    const unreachable = isServiceUnreachable(error);
    content = (
      <StatePanel
        variant="error"
        title={formatMessage(
          unreachable
            ? commonMessages.unreachableTitle
            : commonMessages.loadFailedTitle
        )}
        body={
          unreachable ? formatMessage(messages.unreachableBody) : error.message
        }
        onRetry={refetch}
      />
    );
  } else if (data.length === 0) {
    content = (
      <StatePanel
        variant="empty"
        title={formatMessage(messages.emptyTitle)}
        body={formatMessage(messages.emptyBody)}
      />
    );
  } else {
    content = (
      <>
        <SummaryCards integrations={data} />
        <Box
          sx={{
            bgcolor: 'background.paper',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          <FilterBar
            status={status}
            onStatus={setStatus}
            query={query}
            onQuery={setQuery}
            vendor={vendor}
            vendors={vendors}
            onVendor={setVendor}
          />
          <ConnectionsTable
            data={filtered}
            onRowClick={(id) => navigateRoute(`${ROUTES.INTEGRATIONS}${id}`)}
          />
        </Box>
      </>
    );
  }

  return (
    <>
      {/* Only the first load replaces the page. The dialog sits outside the
          loader so the refetch after a first registration can't unmount it. */}
      <ContentLoader loading={loading && data.length === 0}>
        {error ? null : (
          <EnvironmentBanner
            count={data.length}
            action={
              <Button
                variant="contained"
                size="small"
                onClick={() => {
                  setRegisterMounted(true);
                  setRegisterOpen(true);
                }}
              >
                {formatMessage(messages.register)}
              </Button>
            }
          />
        )}
        {content}
      </ContentLoader>
      {registerMounted ? (
        <RegisterConnectionDialog
          open={registerOpen}
          onClose={() => setRegisterOpen(false)}
          onRegistered={(zone) =>
            enqueueSnackbar({
              variant: 'success',
              message: formatMessage(messages.registered, { zone }),
            })
          }
        />
      ) : null}
    </>
  );
}
